import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '@workspace/ui/components/button';
import { Play, Terminal, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '~/auth/use-auth';
import { ConnectAgentDialog } from '~/components/connect-agent-dialog';
import { useKycGate } from '~/components/kyc-gate';
import { Layout } from '~/components/layouts/layout';
import { ENABLE_CHAT } from '~/config/environment';
import { m } from '~/paraglide/messages.js';
import { AccountBalanceSummary } from '../(payment-methods)/cards/(details)/-components/account-balance-summary';
import { Transactions } from '../(payment-methods)/cards/(details)/-components/transactions';
import { Chat } from './-components/chat';
import { useBalances, useTransactionsAggregated } from './-hooks/use-accounts';

export const Route = createFileRoute('/_authenticated/(home)/')({
  component: DashboardPage,
});

function DashboardPage() {
  const navigate = useNavigate();
  const { user, updateMetadata } = useAuth();
  const { data, isLoading: isLoadingBalance } = useBalances();
  const balance = data?.data?.balance;
  const assetPairs = Object.keys(balance ?? {});

  const [asset, setAsset] = useState('');
  const [transactionTab, setTransactionTab] = useState<string>('todas');
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const { isVerified, openKycModal, KycModal } = useKycGate();

  const showAgentBanner =
    !user?.metadata?.agent_connected && !user?.metadata?.agent_banner_dismissed;

  const handleDismissBanner = () => {
    updateMetadata({ agent_banner_dismissed: true });
  };

  const direction =
    transactionTab === 'entrantes'
      ? 'in'
      : transactionTab === 'salientes'
        ? 'out'
        : undefined;

  const { data: transactionsResponse, isLoading: isLoadingTransactions } =
    useTransactionsAggregated(asset, direction);
  const transactions = transactionsResponse?.data?.transactions ?? [];

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!asset && assetPairs.length > 0) {
      setAsset(assetPairs[0]);
    }
  }, [assetPairs]);

  const handleActivateCard = () => {
    if (isVerified) {
      navigate({ to: '/cards' });
    } else {
      openKycModal();
    }
  };

  return (
    <Layout
      title={m['dashboard.title']()}
      breadcrumbs={[{ label: m['dashboard.title'](), href: '/' }]}
    >
      <main className="container mx-auto p-4 max-w-5xl">
        {showAgentBanner && (
          <div className="relative flex items-center gap-4 rounded-lg border border-primary/20 bg-primary/5 p-4 mb-6">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Terminal className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-foreground">
                {m['connect_agent.banner_title']()}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {m['connect_agent.banner_description']()}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setConnectDialogOpen(true)}
              className="shrink-0"
            >
              {m['connect_agent.banner_cta']()}
            </Button>
            <button
              type="button"
              onClick={handleDismissBanner}
              className="absolute top-2 right-2 rounded-md p-1 text-muted-foreground/60 transition-colors hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {ENABLE_CHAT && (
          <div className="h-full flex flex-col mb-8">
            <Chat maxWidth="5xl" />
          </div>
        )}

        {!balance ||
          (Object.keys(balance).length > 0 && (
            <AccountBalanceSummary
              balance={balance}
              isLoading={isLoadingBalance}
              onChangeAsset={(asset) => {
                setAsset(asset);
              }}
            />
          ))}

        {!balance ||
          (Object.keys(balance).length === 0 && (
            <div className="border border-input rounded-lg p-5 mb-8">
              <h2 className="text-2xl font-semibold">
                {m['dashboard.balances.no_cards_title']()}
              </h2>

              <div className="flex flex-col items-center justify-center">
                <img src="/images/home/card.png" alt="card" />

                <Button
                  className="gap-2"
                  size="lg"
                  variant="outline"
                  onClick={handleActivateCard}
                >
                  <Play className="w-4 h-4" />
                  {m['dashboard.balances.activate_button']()}
                </Button>
              </div>
            </div>
          ))}

        <Transactions
          title={m['dashboard.transactions.recent']()}
          isLoading={isLoadingTransactions}
          transactions={transactions}
          tab={transactionTab}
          onTabChange={setTransactionTab}
        />
      </main>

      <KycModal />
      <ConnectAgentDialog
        open={connectDialogOpen}
        onOpenChange={setConnectDialogOpen}
      />
    </Layout>
  );
}
